import React from 'react';
import { ArrowRight, Award, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mockData';

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-amber-400 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl animate-pulse delay-700"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center pt-16">
          {/* Left Content */}
          <div className="text-white space-y-6">
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight">
              Driving Africa's
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-600">
                Socio-Economic Growth
              </span>
            </h1>

            <p className="text-lg text-gray-300 leading-relaxed">
              {companyInfo.tagline}
            </p>

            <p className="text-base text-gray-400">
              Service Management Consultancy (SMC) is dedicated to using research, innovation, and strategy to facilitate Africa's sustainable development.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-amber-400 to-yellow-600 text-slate-900 hover:from-amber-500 hover:to-yellow-700 font-semibold text-lg px-8 py-6 group"
              >
                Start Your Project
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
              <Button
                onClick={() => scrollToSection('services')}
                variant="outline"
                className="border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-slate-900 font-semibold text-lg px-8 py-6"
              >
                Explore Services
              </Button>
            </div>
          </div>

          {/* Right Content - Feature Cards */}
          <div className="relative hidden lg:block">
            <div className="space-y-4">
              {/* Card 1 */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 transform hover:scale-105 transition-transform duration-300 min-h-[140px] flex items-center">
                <div className="flex items-center space-x-4 w-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="text-slate-900" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-bold text-base mb-2">Strategic Excellence</h3>
                    <p className="text-gray-300 text-sm leading-snug">Innovative solutions driving sustainable socio-economic development across Africa.</p>
                  </div>
                </div>
              </div>

              {/* Card 2 */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 transform hover:scale-105 transition-transform duration-300 min-h-[140px] flex items-center">
                <div className="flex items-center space-x-4 w-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-bold text-base mb-2">Proven Track Record</h3>
                    <p className="text-gray-300 text-sm leading-snug">Trusted by federal ministries, international organizations, and major corporations.</p>
                  </div>
                </div>
              </div>

              {/* Card 3 */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 transform hover:scale-105 transition-transform duration-300 min-h-[140px] flex items-center">
                <div className="flex items-center space-x-4 w-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="text-white" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                      <path d="M2 17l10 5 10-5"></path>
                      <path d="M2 12l10 5 10-5"></path>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-bold text-base mb-2">Expert Team</h3>
                    <p className="text-gray-300 text-sm leading-snug">16+ dedicated experts with 100+ years cumulative experience in diverse sectors.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
